import { useState, useEffect } from "react";
import {
  Form,
  Alert,
  Container,
  Row,
  Col,
  Spinner,
  Button,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import { useLeague } from "../hooks/useLeague";
import { useRequest } from "../hooks/useRequest";
import {
  getSeasonsUrl,
  getActivateSeasonUrl,
  getDeactivateSeasonUrl,
  getLeagueUrl,
  getSeasonUrl,
  getTimeZonesUrl,
} from "../config/api";
import { apiPost, apiPatch, apiDelete } from "../utils/apiClient";
import { PaginatedResponse } from "../types/api";
import { Season, TimeZoneOption } from "../types/models";
import { MAX_PERIOD_MINUTES, formatClock } from "../utils/gameClock";

/** A league cannot play more periods than the API will accept. */
const MAX_PERIODS = 9;

/** Blank rows are dropped on save, so a removed period simply empties. */
function parsePeriodMinutes(values: string[]): number[] {
  return values
    .filter((value) => value.trim() !== "")
    .map((value) => Number(value));
}

function formatGameType(
  gameType: "regular" | "exhibition" | "playoff",
): string {
  const labels = {
    regular: "Regular Season",
    exhibition: "Exhibition",
    playoff: "Playoffs",
  };
  return labels[gameType];
}

function AdminLeagueSettingsPage() {
  const auth = useAuth();
  const { currentLeague, refreshLeagues } = useLeague();
  const { canManageLeague } = useLeagueMembership();
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditingLeague, setIsEditingLeague] = useState(false);
  const [leagueName, setLeagueName] = useState("");
  const [leagueAbbr, setLeagueAbbr] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [timeZone, setTimeZone] = useState("");
  // One field per period, held as strings so a half-typed value is editable.
  const [periodMinutes, setPeriodMinutes] = useState<string[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null);
  const [originalActiveSeasonId, setOriginalActiveSeasonId] = useState<
    number | null
  >(null);
  const [selectedGameType, setSelectedGameType] = useState<
    "regular" | "exhibition" | "playoff"
  >("regular");
  const [originalGameType, setOriginalGameType] = useState<
    "regular" | "exhibition" | "playoff"
  >("regular");
  const [activeSeasonRefreshKey, setActiveSeasonRefreshKey] = useState(0);

  const seasonsUrl = currentLeague
    ? getSeasonsUrl(currentLeague.id)
    : undefined;

  const { data: seasonsResponse, error: seasonsError } = useRequest<
    PaginatedResponse<any>
  >(seasonsUrl, auth.token);

  const seasons = seasonsResponse?.data;

  // Fetch active season details to get game_type
  const activeSeasonUrl =
    currentLeague && currentLeague.active_season_id
      ? `${getSeasonUrl(
          currentLeague.active_season_id,
        )}?_refresh=${activeSeasonRefreshKey}`
      : undefined;

  const { data: activeSeason } = useRequest<Season>(
    activeSeasonUrl,
    auth.token,
  );

  // Reference list for the timezone picker. Public data, so it needs no league.
  const { data: timeZones } = useRequest<TimeZoneOption[]>(
    getTimeZonesUrl(),
    auth.token,
    undefined,
    true, // skip the App-League header: this is global reference data
  );

  // Initialize form fields when league or active season changes
  useEffect(() => {
    if (currentLeague) {
      setLeagueName(currentLeague.name);
      setLeagueAbbr(currentLeague.abbr);
      setCustomDomain(currentLeague.custom_domain || "");
      setTimeZone(currentLeague.time_zone);
      setPeriodMinutes((currentLeague.period_minutes ?? []).map(String));
      setSelectedSeasonId(currentLeague.active_season_id);
      setOriginalActiveSeasonId(currentLeague.active_season_id);

      // Initialize game type from active season
      if (activeSeason) {
        setSelectedGameType(activeSeason.game_type);
        setOriginalGameType(activeSeason.game_type);
      }

      console.log("Initialized league settings form with:", {
        name: currentLeague.name,
        abbr: currentLeague.abbr,
        custom_domain: currentLeague.custom_domain,
        active_season_id: currentLeague.active_season_id,
        game_type: activeSeason?.game_type,
      });
    }
  }, [currentLeague, activeSeason]);

  const saveActiveSeasonChange = async () => {
    if (!currentLeague) return;

    // Only save if the active season has changed
    if (selectedSeasonId === originalActiveSeasonId) {
      return;
    }

    if (selectedSeasonId) {
      // Activate the selected season
      await apiPost(
        getActivateSeasonUrl(selectedSeasonId),
        {},
        { token: auth.token, leagueAbbr: currentLeague.abbr },
      );
    } else if (originalActiveSeasonId) {
      // Clear the active season using DELETE /seasons/:id/activate
      await apiDelete(getDeactivateSeasonUrl(originalActiveSeasonId), {
        token: auth.token,
        leagueAbbr: currentLeague.abbr,
      });
    }
  };

  const saveGameTypeChange = async () => {
    if (!currentLeague || !selectedSeasonId) return;

    // Only save if the game type has changed
    if (selectedGameType === originalGameType) {
      return;
    }

    // Update the season's game_type
    await apiPatch(
      getSeasonUrl(selectedSeasonId),
      { season: { game_type: selectedGameType } },
      { token: auth.token, leagueAbbr: currentLeague.abbr },
    );
  };

  const handleClearActiveSeason = () => {
    setSelectedSeasonId(null);
    // Reset game type to default when clearing active season
    setSelectedGameType("regular");
  };

  const handleSaveLeagueInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentLeague) return;

    setIsUpdating(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Save league info (name, abbr, and custom_domain)
      await apiPatch(
        getLeagueUrl(currentLeague.id),
        {
          league: {
            name: leagueName,
            abbr: leagueAbbr,
            custom_domain: customDomain || null,
            time_zone: timeZone,
            period_minutes: parsePeriodMinutes(periodMinutes),
          },
        },
        { token: auth.token, leagueAbbr: currentLeague.abbr },
      );

      // Save active season if changed
      await saveActiveSeasonChange();

      // Save game type if changed (only if active season is set)
      await saveGameTypeChange();

      // Refresh leagues list - this will trigger LeagueProvider to sync currentLeague
      await refreshLeagues();

      // Refresh active season data to show updated game_type
      setActiveSeasonRefreshKey((prev) => prev + 1);

      setSuccessMessage("League settings updated successfully");
      setIsEditingLeague(false);
    } catch (error) {
      console.error("Failed to update league:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to update league",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingLeague(false);
    if (currentLeague) {
      setLeagueName(currentLeague.name);
      setLeagueAbbr(currentLeague.abbr);
      setCustomDomain(currentLeague.custom_domain || "");
      setTimeZone(currentLeague.time_zone);
      setPeriodMinutes((currentLeague.period_minutes ?? []).map(String));
      setSelectedSeasonId(currentLeague.active_season_id);
      setOriginalActiveSeasonId(currentLeague.active_season_id);
      if (activeSeason) {
        setSelectedGameType(activeSeason.game_type);
        setOriginalGameType(activeSeason.game_type);
      }
    }
    setErrorMessage("");
    setSuccessMessage("");
  };

  if (!currentLeague) {
    return (
      <div className="admin-content">
        <Alert variant="warning">No league selected</Alert>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <h2 className="mb-4 d-flex justify-content-between align-items-center mb-3">
        League Settings{" "}
        {!isEditingLeague && canManageLeague && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setIsEditingLeague(true)}
            disabled={isUpdating}
          >
            <i className="bi-pencil me-1"></i>
            Edit
          </Button>
        )}
      </h2>

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      <Container>
        <Row>
          <Col md={8}>
            <div className="d-flex justify-content-between align-items-center mb-3"></div>

            {!isEditingLeague || !canManageLeague ? (
              <>
                <div className="mb-4">
                  <p>
                    <strong>Name:</strong> {currentLeague.name}
                  </p>
                  <p>
                    <strong>Abbreviation:</strong> {currentLeague.abbr}
                  </p>
                  <p>
                    <strong>Custom Domain:</strong>{" "}
                    {currentLeague.custom_domain || "None"}
                  </p>
                  <p>
                    <strong>Time Zone:</strong> {currentLeague.time_zone}
                  </p>
                  <p>
                    <strong>Periods:</strong>{" "}
                    {describePeriods(currentLeague.period_minutes)}
                  </p>
                  <p>
                    <strong>Active Season:</strong>{" "}
                    {currentLeague.active_season_id
                      ? seasons?.find(
                          (s: any) => s.id === currentLeague.active_season_id,
                        )?.name || "Loading..."
                      : "None"}
                  </p>
                  {currentLeague.active_season_id && activeSeason && (
                    <p>
                      <strong>Season Mode:</strong>{" "}
                      {formatGameType(activeSeason.game_type)}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <Form onSubmit={handleSaveLeagueInfo} className="mb-4">
                <Form.Group className="mb-3">
                  <Form.Label>League Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={leagueName}
                    onChange={(e) => setLeagueName(e.target.value)}
                    required
                    disabled={isUpdating}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Abbreviation</Form.Label>
                  <Form.Control
                    type="text"
                    value={leagueAbbr}
                    onChange={(e) => setLeagueAbbr(e.target.value)}
                    required
                    disabled={isUpdating}
                    maxLength={10}
                  />
                  <Form.Text className="text-muted">
                    Short abbreviation for your league (e.g., "WOHL", "BL")
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Custom Domain</Form.Label>
                  <Form.Control
                    type="text"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    disabled={isUpdating}
                    placeholder="example.com"
                  />
                  <Form.Text className="text-muted">
                    Optional custom domain for your league (bare hostname only,
                    e.g., "myleague.com")
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="league-time-zone">
                  <Form.Label>Time Zone</Form.Label>
                  <Form.Select
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    disabled={isUpdating || !timeZones}
                  >
                    {/* Keep the league's own zone selectable while the list
                        loads, so saving can't silently change it. */}
                    {timeZones ? (
                      timeZones.map((zone) => (
                        <option key={zone.name} value={zone.name}>
                          {zone.name} (UTC{zone.offset})
                        </option>
                      ))
                    ) : (
                      <option value={timeZone}>{timeZone}</option>
                    )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    The zone game times are in. Calendar feeds use it to place
                    games correctly for people in other time zones.
                  </Form.Text>
                </Form.Group>

                <PeriodLengthsField
                  values={periodMinutes}
                  disabled={isUpdating}
                  onChange={setPeriodMinutes}
                />

                {!seasons && <Spinner animation="border" size="sm" />}
                {seasonsError && (
                  <Alert variant="danger">Failed to load seasons</Alert>
                )}
                {seasons && seasons.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Active Season</Form.Label>
                    <div className="d-flex gap-2 align-items-start">
                      <Form.Select
                        value={selectedSeasonId ?? ""}
                        onChange={(e) =>
                          setSelectedSeasonId(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        disabled={isUpdating}
                        style={{ flex: 1 }}
                      >
                        <option value="">None</option>
                        {seasons.map((season: any) => (
                          <option key={season.id} value={season.id}>
                            {season.name}
                          </option>
                        ))}
                      </Form.Select>
                      {selectedSeasonId && (
                        <Button
                          variant="outline-secondary"
                          onClick={handleClearActiveSeason}
                          disabled={isUpdating}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      The active season will be displayed on the public league
                      pages
                    </Form.Text>
                  </Form.Group>
                )}

                {selectedSeasonId && (
                  <Form.Group className="mb-3">
                    <Form.Label>Season Mode</Form.Label>
                    <Form.Select
                      value={selectedGameType}
                      onChange={(e) =>
                        setSelectedGameType(
                          e.target.value as
                            | "regular"
                            | "exhibition"
                            | "playoff",
                        )
                      }
                      disabled={isUpdating}
                    >
                      <option value="regular">Regular Season</option>
                      <option value="exhibition">Exhibition</option>
                      <option value="playoff">Playoffs</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Controls which games and standings are displayed on public
                      pages
                    </Form.Text>
                  </Form.Group>
                )}

                <div className="d-flex gap-2">
                  <Button variant="primary" type="submit" disabled={isUpdating}>
                    {isUpdating ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelEdit}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}

/** "Three 20-minute periods", or the lengths listed when they differ. */
function describePeriods(minutes: number[] | undefined): string {
  if (!minutes || minutes.length === 0) return "Not set";

  const count = minutes.length;
  const noun = count === 2 ? "halves" : "periods";
  const allSame = minutes.every((value) => value === minutes[0]);

  return allSame
    ? `${count} × ${minutes[0]} minutes`
    : `${count} ${noun}: ${minutes.join(", ")} minutes`;
}

/**
 * The lengths of a game's periods, one field each.
 *
 * How many fields there are is how many periods a game has, so a league that
 * plays two longer halves removes a row rather than setting a separate count,
 * and a league whose first period is shorter just types a different number.
 * A period past the last row is overtime, whose length varies by competition
 * and is left to the scorekeeper.
 */
function PeriodLengthsField({
  values,
  disabled,
  onChange,
}: {
  values: string[];
  disabled: boolean;
  onChange: (values: string[]) => void;
}) {
  const totalMinutes = values.reduce(
    (sum, value) => sum + (Number(value) || 0),
    0,
  );

  const setAt = (index: number, value: string) => {
    const next = [...values];
    next[index] = value.replace(/\D/g, "").slice(0, 2);
    onChange(next);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>Period lengths</Form.Label>
      <div className="d-flex flex-wrap gap-2 align-items-end">
        {values.map((value, index) => (
          <div key={index} style={{ width: "5.5rem" }}>
            <Form.Label
              className="text-muted mb-1"
              style={{ fontSize: "0.75rem" }}
              htmlFor={`period-minutes-${index}`}
            >
              Period {index + 1}
            </Form.Label>
            <div className="d-flex gap-1 align-items-center">
              <Form.Control
                id={`period-minutes-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                value={value}
                disabled={disabled}
                onChange={(e) => setAt(index, e.target.value)}
                className="text-center"
              />
            </div>
          </div>
        ))}
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={disabled || values.length <= 1}
          onClick={() => onChange(values.slice(0, -1))}
        >
          <i className="bi bi-dash" aria-hidden="true" /> Remove
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={disabled || values.length >= MAX_PERIODS}
          onClick={() =>
            onChange([...values, values[values.length - 1] ?? "20"])
          }
        >
          <i className="bi bi-plus" aria-hidden="true" /> Add
        </Button>
      </div>
      <Form.Text className="text-muted">
        In minutes, one per period — three periods for most leagues, two for a
        league that plays halves. This is how many periods a game has and what
        the scoreboard clock starts each one at; anything past the last one is
        overtime. Each period can be 1 to {MAX_PERIOD_MINUTES} minutes.
        {totalMinutes > 0 &&
          ` Currently ${formatClock(totalMinutes * 60)} of play.`}
      </Form.Text>
    </Form.Group>
  );
}

export default AdminLeagueSettingsPage;
